import { useState } from "react";
import { db, eq } from "../lib/api";
import { invalidate, useDebounced, useQuery } from "../lib/hooks";
import { AREA_LABEL, LEAD_AREAS, type Area } from "../lib/refdata";
import { useSession } from "../lib/session";
import { MergeSheet } from "../pages/leads/Cleanup";
import { Sheet, useToast } from "./ui";

type Props = {
  person: { id: string; full_name: string };
  ws: { is_team: boolean; is_lead: boolean; is_client: boolean } | null;
  areas: { area: Area }[];
  member: { member_id: string; recruiter: string | null; agent_code: string | null } | null;
  tags: { tag_id: string; tags: { name: string } }[];
  onClose: () => void;
};

/** "Who is this person": lead / team member / client, tags, and (for the Owner or a granted account) merge & remove. */
export function PersonType({ person, ws, areas, member, tags, onClose }: Props) {
  const { can } = useSession();
  const toast = useToast();
  const [edit, setEdit] = useState<"" | "lead" | "team" | "move">("");
  const events = useQuery(`person:${person.id}:events`, () => db.select<{ event_id: string; events: { name: string } | null }>("event_people", { person_id: eq(person.id), select: "event_id,events(name)" }));
  const [err, setErr] = useState("");
  const [merge, setMerge] = useState<string | null>(null);
  const [pickMerge, setPickMerge] = useState(false);
  const [removing, setRemoving] = useState(false);
  const leadAreas = areas.filter((a) => LEAD_AREAS.includes(a.area));
  const admin = can("people_admin", "edit");
  const refresh = () => {
    invalidate(`person:${person.id}`); invalidate("people"); invalidate("today"); invalidate("dash:"); invalidate("team"); invalidate("clients"); invalidate("cleanup");
  };
  async function run(fn: string, args: Record<string, unknown>, msg: string) {
    setErr("");
    try {
      const r = await db.rpc<{ ok: boolean; reason?: string }>(fn, { p_person: person.id, ...args });
      if (r && r.ok === false) { setErr(r.reason ?? "Not changed"); return false; }
      refresh(); toast(msg); setEdit(""); return true;
    } catch (e) { setErr((e as Error).message); return false; }
  }

  async function moveTo(a: Area) {
    setErr("");
    try {
      const from = leadAreas.length === 1 ? leadAreas[0].area : leadAreas.some((x) => x.area === "networking") ? "networking" : null;
      await db.rpc("people_move_list", { p_people: [person.id], p_to: a, p_from: from });
      refresh(); toast(`${person.full_name} moved to ${AREA_LABEL[a]}`); setEdit("");
    } catch (e) { setErr((e as Error).message); }
  }
  async function offEvent(id: string, name: string) {
    setErr("");
    try { await db.rpc("event_remove_person", { p_event: id, p_person: person.id }); invalidate(`person:${person.id}`); invalidate("events"); toast(`Taken off ${name}`); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <div className="ptype">
      <div className="sol-head"><strong>Who is this person</strong></div>
      <div className="ptype-rows">
        {/* Lead */}
        <div className="ptype-row">
          <div><b>Lead</b><span className="small muted">{ws?.is_lead ? leadAreas.map((a) => AREA_LABEL[a.area]).join(", ") : "Not on a lead list"}</span></div>
          {can("leads", "edit") && (ws?.is_lead
            ? <span className="ptype-btns">
                <button className="btn small" onClick={() => setEdit(edit === "move" ? "" : "move")}>Move to…</button>
                <button className="btn small ghost" onClick={() => run("person_set_lead", { p_on: false }, `${person.full_name} is off the lead lists`)}>Not a lead</button>
              </span>
            : <button className="btn small" onClick={() => setEdit(edit === "lead" ? "" : "lead")}>Make a lead</button>)}
        </div>
        {edit === "move" && (
          <div className="ptype-edit">
            <span className="small muted">Move from {leadAreas.map((a) => AREA_LABEL[a.area]).join(", ")} to</span>
            {LEAD_AREAS.filter((a) => !leadAreas.some((x) => x.area === a)).map((a) => <button key={a} className="btn small" onClick={() => moveTo(a)}>{AREA_LABEL[a]}</button>)}
          </div>
        )}
        {/* Events */}
        {!!events.data?.length && (
          <div className="ptype-row">
            <div><b>Events</b><span className="small muted">Met at {events.data.map((x) => x.events?.name).join(", ")}</span></div>
            {can("events", "edit") && <span className="ptype-btns">{events.data.map((x) => (
              <button key={x.event_id} className="btn small ghost" title="Added to this event by mistake" onClick={() => offEvent(x.event_id, x.events?.name ?? "the event")}>Not from {x.events?.name}</button>
            ))}</span>}
          </div>
        )}
        {edit === "lead" && (
          <div className="ptype-edit">
            <span className="small muted">Add to</span>
            {LEAD_AREAS.map((a) => <button key={a} className="btn small" onClick={() => run("person_set_lead", { p_on: true, p_area: a }, `Added to ${AREA_LABEL[a]}`)}>{AREA_LABEL[a]}</button>)}
          </div>
        )}
        {/* Team */}
        <div className="ptype-row">
          <div><b>Team member</b><span className="small muted">{ws?.is_team ? `Recruited by ${member?.recruiter ?? "(not set)"}${member?.agent_code ? ` · ${member.agent_code}` : ""}` : "Not on the team"}</span></div>
          {can("team", "edit") && <span className="ptype-btns">
            <button className="btn small" onClick={() => setEdit(edit === "team" ? "" : "team")}>{ws?.is_team ? "Change recruiter" : "Make team member"}</button>
            {ws?.is_team && <button className="btn small ghost" onClick={() => run("person_set_team", { p_on: false }, `${person.full_name} is off the team`)}>Not on the team</button>}
          </span>}
        </div>
        {edit === "team" && <TeamEdit isTeam={!!ws?.is_team} onSave={(rec, code) => run("person_set_team", { p_on: true, p_recruiter: rec, p_agent_code: code || null },
          ws?.is_team ? "Recruiter updated" : `${person.full_name} added to the team`)} onCancel={() => setEdit("")} />}
        {/* Client */}
        <div className="ptype-row">
          <div><b>Client</b><span className="small muted">{ws?.is_client ? "Has a client file" : "Not a client"}</span></div>
          {can("clients", "edit") && (ws?.is_client
            ? <button className="btn small ghost" onClick={() => run("person_set_client", { p_on: false }, `${person.full_name} is no longer a client`)}>Not a client</button>
            : <button className="btn small" onClick={() => run("person_set_client", { p_on: true }, `${person.full_name} is now a client`)}>Make client</button>)}
        </div>
      </div>
      <TagEditor person={person} tags={tags} onChange={refresh} />
      {err && <div className="error-box" role="alert" style={{ marginTop: 8 }}>{err}</div>}
      {admin && (
        <div className="toolbar ptype-admin">
          <button className="btn small" onClick={() => setPickMerge(true)}>Merge with…</button>
          <button className="btn small ghost danger" onClick={() => setRemoving(true)}>Remove…</button>
        </div>
      )}
      {pickMerge && <PickPerson exclude={person.id} onClose={() => setPickMerge(false)} onPick={(id) => { setPickMerge(false); setMerge(id); }} />}
      {merge && <MergeSheet c={{ person_id: person.id, duplicate_of: [merge] }} onClose={() => setMerge(null)}
        onDone={() => { setMerge(null); refresh(); toast("Merged"); }} />}
      {removing && <RemoveSheet person={person} onClose={() => setRemoving(false)} onDone={() => { setRemoving(false); refresh(); onClose(); }} />}
    </div>
  );
}

function TeamEdit({ isTeam, onSave, onCancel }: { isTeam: boolean; onSave: (recruiter: string | null, code: string) => void; onCancel: () => void }) {
  const [q, setQ] = useState("");
  const [rec, setRec] = useState<{ member_id: string; full_name: string } | null>(null);
  const [code, setCode] = useState("");
  const dq = useDebounced(q, 250);
  const opts = useQuery(`recruiters:${dq}`, () => db.rpc<{ member_id: string; full_name: string; agent_code: string | null; recruiter: string | null }[]>("team_recruiter_options", { p_q: dq }));
  return (
    <div className="ptype-edit col">
      <label className="small">Their recruiter (the person who brought them in, at any level)</label>
      {rec ? (
        <div className="toolbar" style={{ margin: 0 }}><span className="pill team">{rec.full_name}</span><button className="btn small ghost" onClick={() => setRec(null)}>Change</button></div>
      ) : <>
        <input className="input" value={q} placeholder="Search the team by name or code" onChange={(e) => setQ(e.target.value)} autoFocus />
        <div className="pick-list">
          {(opts.data ?? []).map((o) => (
            <button key={o.member_id} className="pick" onClick={() => setRec(o)}>
              <b>{o.full_name}</b><span className="small muted">{[o.agent_code, o.recruiter && `under ${o.recruiter}`].filter(Boolean).join(" · ")}</span>
            </button>
          ))}
        </div>
      </>}
      {!isTeam && <input className="input" value={code} placeholder="Agent code (optional)" onChange={(e) => setCode(e.target.value)} />}
      <div className="toolbar" style={{ margin: 0 }}>
        <button className="btn small primary" disabled={!rec} onClick={() => onSave(rec!.member_id, code)}>{isTeam ? "Save recruiter" : "Add to team"}</button>
        <button className="btn small ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function TagEditor({ person, tags, onChange }: { person: { id: string }; tags: Props["tags"]; onChange: () => void }) {
  const { can } = useSession();
  const all = useQuery("tags:all", () => db.select<{ id: string; name: string }>("tags", { select: "id,name", order: "name" }));
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const editable = can("leads", "edit") || can("team", "edit") || can("clients", "edit");
  const have = new Set(tags.map((t) => t.tag_id));
  async function add(tagId: string) {
    setErr("");
    try { await db.insert("person_tags", { person_id: person.id, tag_id: tagId }); onChange(); setAdding(false); setName(""); }
    catch (e) { setErr((e as Error).message); }
  }
  async function create() {
    const v = name.trim();
    if (!v) return;
    const existing = (all.data ?? []).find((t) => t.name.toLowerCase() === v.toLowerCase());
    if (existing) return add(existing.id);
    try { const [t] = await db.insert<{ id: string }>("tags", { name: v }); invalidate("tags:"); invalidate("nl:tags"); await add(t.id); }
    catch (e) { setErr((e as Error).message); }
  }
  async function drop(tagId: string) {
    await db.remove("person_tags", { person_id: eq(person.id), tag_id: eq(tagId) }); onChange();
  }
  const sugg = (all.data ?? []).filter((t) => !have.has(t.id) && (!name || t.name.toLowerCase().includes(name.toLowerCase()))).slice(0, 10);
  return (
    <div className="ptype-tags">
      <b className="small">Tags</b>
      <div className="chips">
        {tags.map((t) => (
          <span key={t.tag_id} className="chip big">{t.tags.name}{editable && <button className="chip-x" aria-label={`Remove tag ${t.tags.name}`} onClick={() => drop(t.tag_id)}>×</button>}</span>
        ))}
        {!tags.length && <span className="small muted">No tags</span>}
        {editable && !adding && <button className="chip big" onClick={() => setAdding(true)}>+ Tag</button>}
      </div>
      {adding && (
        <div className="ptype-edit col">
          <input className="input" autoFocus value={name} placeholder="Type a tag (new or existing)" onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") create(); if (e.key === "Escape") setAdding(false); }} />
          <div className="chips">{sugg.map((t) => <button key={t.id} className="chip big" onClick={() => add(t.id)}>{t.name}</button>)}
            {name.trim() && !sugg.some((t) => t.name.toLowerCase() === name.trim().toLowerCase()) && <button className="chip big on" onClick={create}>Create “{name.trim()}”</button>}</div>
          <div><button className="btn small ghost" onClick={() => { setAdding(false); setName(""); }}>Done</button></div>
        </div>
      )}
      {err && <div className="small" style={{ color: "var(--alert)" }}>{err}</div>}
    </div>
  );
}

function PickPerson({ exclude, onPick, onClose }: { exclude: string; onPick: (id: string) => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 250);
  const found = useQuery(dq.length > 1 ? `merge-find:${dq}` : null, () =>
    db.select<{ id: string; full_name: string; email: string | null; phone: string | null; known_as: string | null }>("people", { select: "id,full_name,email,phone,known_as", or: `(full_name.ilike.*${dq.replace(/[*,()]/g, " ")}*,known_as.ilike.*${dq.replace(/[*,()]/g, " ")}*)`, deleted_at: "is.null", limit: 12, order: "full_name" }));
  return (
    <Sheet title="Merge with…" onClose={onClose}>
      <p className="small muted" style={{ marginTop: 0 }}>Find the duplicate record. On the next screen you choose which one to keep.</p>
      <input className="input" autoFocus value={q} placeholder="Search by name" onChange={(e) => setQ(e.target.value)} />
      <div className="pick-list" style={{ marginTop: 10 }}>
        {(found.data ?? []).filter((p) => p.id !== exclude).map((p) => (
          <button key={p.id} className="pick" onClick={() => onPick(p.id)}><b>{p.full_name || "(no name)"}{p.known_as && <span className="known-as"> · {p.known_as}</span>}</b><span className="small muted">{[p.phone, p.email].filter(Boolean).join(" · ")}</span></button>
        ))}
      </div>
    </Sheet>
  );
}

function RemoveSheet({ person, onClose, onDone }: { person: { id: string; full_name: string }; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true); setErr("");
    try {
      const r = await db.rpc<{ ok: boolean; reason?: string; meetings_cancelled?: number }>("person_remove", { p_person: person.id, p_reason: reason || null });
      if (!r.ok) { setErr(r.reason ?? "Not removed"); return; }
      toast(`${person.full_name} removed${r.meetings_cancelled ? ` (${r.meetings_cancelled} upcoming meeting${r.meetings_cancelled > 1 ? "s" : ""} cancelled)` : ""}. Restore from Removed within 30 days.`);
      onDone();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  return (
    <Sheet title={`Remove ${person.full_name}?`} onClose={onClose} footer={<>
      <button className="btn" onClick={onClose}>Keep</button>
      <button className="btn primary danger-fill" disabled={busy} onClick={go}>{busy ? "Removing…" : "Remove"}</button>
    </>}>
      <p style={{ marginTop: 0 }}>They disappear from every list, pipeline and report. Their upcoming meetings are cancelled.</p>
      <p className="small muted">You can bring them back from <b>Leads → Removed</b> for 30 days. After that they're erased for good. If this is a duplicate, use <b>Merge with…</b> instead so their history isn't lost.</p>
      <label className="field"><span>Why (optional)</span><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Test record, asked to be forgotten" /></label>
      {err && <div className="error-box" role="alert">{err}</div>}
    </Sheet>
  );
}

